import { Fragment, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AtSign, ChevronRight, ChevronsUp, Ellipsis, History, Link2, List, ListFilter, MessageSquare, PanelRightClose, Paperclip, Reply, Search, SendHorizontal, Smile, SmilePlus, Trash2 } from 'lucide-react';
import { flattenPages } from '@missions/api-client';
import type { Comment, MissionDetail, TimelineItem } from '@missions/types';
import { useDialogs, useToast, useUnsavedChanges } from '@missions/ui';
import { api } from '../../lib/client';
import { useErrorToast, useInfinite, useInvalidate } from '../../lib/hooks';
import { useSession, useT } from '../../lib/session';
import { Av, Radar, exact } from '../../components/atoms';
import { Menu, type MenuItem } from '../../components/Menu';
import { useMemberOptions } from './data';

type Filter = 'all' | 'comments' | 'activity' | 'mentions';
const MIN = 6e4;
const uRel = (iso: string) => {
  const d = Date.now() - new Date(iso).getTime();
  if (d < MIN) return 'Just now';
  if (d < 36e5) return `${Math.round(d / MIN)}m`;
  if (d < 864e5) return `${Math.round(d / 36e5)}h`;
  if (d < 7 * 864e5) return `${Math.round(d / 864e5)}d`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

type Act = Extract<TimelineItem, { kind: 'activity' }>;

function activityText(a: { action: string; data: Record<string, unknown> }, item: string, subItem: string): ReactNode {
  const d = a.data as Record<string, string | undefined>;
  const pill = (txt?: string) => <span className="st" style={{ background: 'var(--text-3)' }}>{txt}</span>;
  switch (a.action) {
    case 'mission.created':
      return <>created this {item}</>;
    case 'status.changed':
      return <>changed status from {pill(d['from'])} → {pill(d['to'])}</>;
    case 'assignee.added':
      return <>assigned <b>{d['name']}</b></>;
    case 'assignee.removed':
      return <>unassigned <b>{d['name']}</b></>;
    case 'due.changed':
      return d['to'] ? <>set the due date to <b>{new Date(`${d['to']}T00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</b></> : <>removed the due date</>;
    case 'priority.changed':
      return <>changed priority to <b style={{ textTransform: 'capitalize' }}>{d['to']}</b></>;
    case 'attachment.added':
      return <>attached <b>{d['fileName']}</b></>;
    case 'objective.completed':
      return <>completed the {subItem} <b>{d['title']}</b></>;
    case 'title.changed':
      return <>renamed this {item}</>;
    default:
      return <>{a.action.replace(/[._]/g, ' ')}</>;
  }
}

const highlight = (body: string) =>
  body.split(/(@[A-Z][\w.-]*(?: [A-Z][\w.-]*)?)/g).map((part, i) => (part.startsWith('@') ? <span className="mention" key={i}>{part}</span> : <Fragment key={i}>{part}</Fragment>));

function CommentCard({ c, missionId, onReply }: { c: Comment; missionId: string; onReply: (name: string) => void }) {
  const s = useSession();
  const toast = useToast();
  const dialogs = useDialogs();
  const invalidate = useInvalidate();
  const onErr = useErrorToast();
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState(false);
  useLayoutEffect(() => {
    const b = bodyRef.current;
    if (b) setLong(b.scrollHeight > b.clientHeight + 1);
  }, [c.body]);
  const thumbs = c.reactions.find((r) => r.emoji === '👍');
  const mine = c.author.id === s.me?.user.id;
  const react = async () => {
    try {
      if (thumbs?.mine) await api.call('comments.unreact', { params: { id: c.id, emoji: '👍' } });
      else await api.call('comments.react', { params: { id: c.id }, body: { emoji: '👍' } });
      await invalidate('timeline');
    } catch (e) {
      onErr(e);
    }
  };
  const items: MenuItem[] = [
    {
      label: 'Copy link to comment',
      icon: <Link2 />,
      onClick: () => {
        void navigator.clipboard?.writeText(`${location.origin}${location.pathname}?m=${missionId}#c-${c.id}`);
        toast('Comment link copied');
      },
    },
    { label: 'Reply', icon: <Reply />, onClick: () => onReply(c.author.name) },
    ...(mine || s.can('comment.delete.any')
      ? (['-', {
          label: 'Delete comment',
          icon: <Trash2 />,
          danger: true,
          onClick: async () => {
            if (await dialogs.confirm({ tone: 'danger', title: 'Delete this comment?', message: 'It will be removed for everyone on this mission.', confirmText: 'Delete comment' })) {
              try {
                await api.call('comments.delete', { params: { id: c.id } });
                await invalidate('timeline', 'missions.get');
                toast('Comment deleted');
              } catch (e) {
                onErr(e);
              }
            }
          },
        }] satisfies MenuItem[])
      : []),
  ];
  return (
    <article className="cm" id={`c-${c.id}`}>
      <div className="cm-h">
        <Av u={c.author} size={22} />
        <b>{c.author.name}</b>
        <time dateTime={c.createdAt} data-tooltip={exact(c.createdAt)}>{uRel(c.createdAt)}</time>
      </div>
      <span className="cm-acts">
        <button type="button" onClick={() => void react()} aria-label="React with thumbs up" data-tooltip="React"><SmilePlus /></button>
        <button type="button" onClick={() => onReply(c.author.name)} aria-label={`Reply to ${c.author.name}`} data-tooltip="Reply"><Reply /></button>
        <button type="button" ref={menuRef} onClick={() => setMenu((o) => !o)} aria-label="More comment actions" data-tooltip="More"><Ellipsis /></button>
      </span>
      <Menu anchorRef={menuRef} open={menu} onClose={() => setMenu(false)} title="Comment" items={items} alignRight minWidth={190} />
      <div className={`cm-b${open ? ' open' : ''}`} ref={bodyRef}>{highlight(c.body)}</div>
      {(long || open) && (
        <button type="button" className="cm-more" onClick={() => setOpen((o) => !o)}>{open ? 'Show less' : 'Show more'}</button>
      )}
      {thumbs && (
        <div className="cm-rx">
          <button type="button" className={thumbs.mine ? 'mine' : ''} aria-pressed={thumbs.mine} onClick={() => void react()}>👍 {thumbs.count}</button>
        </div>
      )}
    </article>
  );
}

function ActRow({ a, you }: { a: { action: string; data: Record<string, unknown>; at: string; actor?: { name: string } | null }; you: boolean }) {
  const t = useT();
  return (
    <div className="ev sys">
      <span className="ev-dot" aria-hidden="true" />
      <span className="ev-t"><b>{you ? 'You' : (a.actor?.name.split(' ')[0] ?? 'Someone')}</b> {activityText(a, t.item, t.subItem)}</span>
      <time dateTime={a.at} data-tooltip={exact(a.at)}>{uRel(a.at)}</time>
    </div>
  );
}

function GroupRow({ a, you }: { a: Act; you: boolean }) {
  const [open, setOpen] = useState(false);
  const all = [{ action: a.action, data: a.data, at: a.at }, ...(a.group ?? [])];
  return (
    <>
      <button type="button" className="ev sys grp" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <ChevronRight className="chev" />
        <span className="ev-t"><b>{you ? 'You' : a.actor?.name.split(' ')[0]}</b> made {all.length} changes</span>
        <time data-tooltip={exact(a.at)}>{uRel(a.at)}</time>
      </button>
      <div className="grp-items" hidden={!open}>
        {all.map((g, i) => <ActRow key={i} a={{ ...g, actor: a.actor }} you={you} />)}
      </div>
    </>
  );
}

/** ClickUp-style Activity column: search, filter, hide, newest-last timeline, pinned composer. */
export function ActivityColumn({ mission, onHide }: { mission: MissionDetail; onHide: () => void }) {
  const s = useSession();
  const toast = useToast();
  const invalidate = useInvalidate();
  const onErr = useErrorToast();
  const members = useMemberOptions();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [searchOn, setSearchOn] = useState(false);
  const [menu, setMenu] = useState(false);
  const filterRef = useRef<HTMLButtonElement>(null);
  const scRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [text, setText] = useState('');
  const [exp, setExp] = useState(false);
  const [sending, setSending] = useState(false);
  const [mentionIds, setMentionIds] = useState<string[]>([]);

  const tl = useInfinite('timeline.list', { params: { id: mission.id }, query: { filter, q: q || undefined, limit: 20 } });
  const items = useMemo(() => [...flattenPages(tl.data)].reverse(), [tl.data]);
  const total = tl.data?.pages[0]?.page.totalApprox ?? items.length;
  useUnsavedChanges(text.trim() !== '', { onSave: () => void post(), onDiscard: () => setText('') });

  // newest at the bottom, like a chat timeline
  useEffect(() => {
    const sc = scRef.current;
    if (sc && !tl.isFetchingNextPage) sc.scrollTop = sc.scrollHeight;
  }, [items.length, tl.isFetchingNextPage]);

  const size = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const min = exp ? 72 : 34;
    ta.style.height = `${Math.max(min, Math.min(ta.scrollHeight, 160))}px`;
    ta.style.overflowY = ta.scrollHeight > 160 ? 'auto' : 'hidden';
  };
  useLayoutEffect(size, [exp, text]);

  const mentionQ = /@(\w*)$/.exec(text)?.[1] ?? null;
  const mentionList = mentionQ === null ? [] : members.options.filter((m) => m.label.toLowerCase().includes(mentionQ.toLowerCase())).slice(0, 5);

  async function post() {
    const body = text.trim();
    if (!body || !s.can('comment.create')) return;
    setSending(true);
    try {
      await api.call('comments.create', { params: { id: mission.id }, body: { body, mentionIds } });
      setText('');
      setMentionIds([]);
      await invalidate('timeline', 'missions.get');
      toast('Comment posted');
    } catch (e) {
      onErr(e);
    } finally {
      setSending(false);
    }
  }

  const filterItems: MenuItem[] = [
    { label: 'All', icon: <List />, selected: filter === 'all', onClick: () => setFilter('all') },
    { label: 'Comments only', icon: <MessageSquare />, selected: filter === 'comments', onClick: () => setFilter('comments') },
    { label: 'Activity only', icon: <History />, selected: filter === 'activity', onClick: () => setFilter('activity') },
    { label: 'My mentions', icon: <AtSign />, selected: filter === 'mentions', onClick: () => setFilter('mentions') },
  ];
  const me = s.me!.user;
  const reply = (name: string) => {
    setText(`@${name} `);
    setExp(true);
    setTimeout(() => {
      const ta = taRef.current;
      ta?.focus();
      ta?.setSelectionRange(ta.value.length, ta.value.length);
    }, 0);
  };

  return (
    <section className="act-col" aria-labelledby="actT">
      <div className="act-h">
        <b className="act-t" id="actT">Activity</b>
        <span className="act-n">{total}</span>
        <span className="sp" />
        <button type="button" className="ib" aria-label="Search activity" aria-pressed={searchOn} data-tooltip="Search" onClick={() => { setSearchOn((o) => !o); if (searchOn) setQ(''); }}>
          <Search />
        </button>
        <button type="button" className="ib" ref={filterRef} aria-label={`Filter activity: ${filter}`} aria-haspopup="menu" data-tooltip="Filter" onClick={() => setMenu((o) => !o)}>
          <ListFilter />
          {filter !== 'all' && <span className="act-fdot" />}
        </button>
        <Menu anchorRef={filterRef} open={menu} onClose={() => setMenu(false)} title="Show" items={filterItems} alignRight minWidth={200} />
        <button type="button" className="ib act-hide" aria-label="Hide activity" data-tooltip="Hide activity" onClick={onHide}><PanelRightClose /></button>
      </div>
      {searchOn && (
        <div className="act-search">
          <input type="search" className="ui-input" placeholder="Search comments & activity…" aria-label="Search comments and activity" autoComplete="off" autoFocus value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      )}
      <div className="act-scroll" ref={scRef}>
        {tl.hasNextPage && (
          <button type="button" className="upd-more" onClick={() => void tl.fetchNextPage()} disabled={tl.isFetchingNextPage}>
            {tl.isFetchingNextPage ? <><Radar size={16} /><span>Loading older…</span></> : <><ChevronsUp /><span>Show older</span></>}
          </button>
        )}
        <div className="feed" aria-live="polite">
          {tl.isPending ? (
            <div className="upd-empty"><Radar /><span>Loading activity…</span></div>
          ) : items.length === 0 ? (
            <div className="upd-empty">
              <Radar />
              <span>{q ? `Nothing matches “${q}”.` : filter === 'activity' ? 'No activity yet.' : filter === 'mentions' ? 'No one has mentioned you here yet.' : 'No comments yet. Start the conversation below.'}</span>
            </div>
          ) : (
            items.map((it) =>
              it.kind === 'comment' ? (
                <CommentCard key={it.id} c={it.comment} missionId={mission.id} onReply={reply} />
              ) : it.group?.length ? (
                <GroupRow key={it.id} a={it} you={it.actor?.id === me.id} />
              ) : (
                <ActRow key={it.id} a={it} you={it.actor?.id === me.id} />
              ),
            )
          )}
        </div>
      </div>
      <form
        className="compose"
        ref={formRef}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void post();
        }}
        onBlur={(e) => {
          const to = e.relatedTarget as Node | null;
          if (to && formRef.current?.contains(to)) return;
          setTimeout(() => {
            if (!formRef.current?.contains(document.activeElement) && !text.trim()) setExp(false);
          }, 0);
        }}
      >
        {mentionList.length > 0 && (
          <div className="mention-pop">
            <div className="mx-h">Mention</div>
            {mentionList.map((m) => (
              <button
                type="button"
                key={m.value}
                className="mx-i"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setText((x) => x.replace(/@(\w*)$/, `@${m.label} `));
                  setMentionIds((ids) => Array.from(new Set([...ids, m.value])));
                  taRef.current?.focus();
                }}
              >
                <span className="av" style={{ background: m.avatarColor, width: 22, height: 22, fontSize: 9 }}>{m.avatar}</span>
                <span className="mx-l">{m.label}</span>
              </button>
            ))}
          </div>
        )}
        <div className={`cmp${exp ? ' exp' : ''}`} onFocus={() => setExp(true)}>
          <div className="cmp-row">
            <Av u={me} size={26} tip={false} />
            <textarea
              ref={taRef}
              rows={1}
              placeholder="Mention @someone or comment…"
              aria-label="Write a comment"
              value={text}
              disabled={!s.can('comment.create')}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void post();
                }
              }}
            />
            <button type="submit" className="cmp-mini" aria-label="Send comment" data-tooltip="Send comment" data-shortcut="⌘↵" onMouseDown={(e) => e.preventDefault()} disabled={sending}>
              <SendHorizontal />
            </button>
          </div>
          <div className="cmp-tools">
            <button type="button" className="ib" aria-label="Mention someone" data-tooltip="Mention someone" data-shortcut="@" onMouseDown={(e) => e.preventDefault()} onClick={() => setText((x) => `${x}${x && !x.endsWith(' ') ? ' ' : ''}@`)}>
              <AtSign />
            </button>
            <button type="button" className="ib" aria-label="Attach file" data-tooltip="Attach file" onMouseDown={(e) => e.preventDefault()} onClick={() => toast('Attach files from the Attachments section of this mission.', { type: 'info' })}>
              <Paperclip />
            </button>
            <button type="button" className="ib" aria-label="Add emoji" data-tooltip="Add emoji" onMouseDown={(e) => e.preventDefault()} onClick={() => setText((x) => `${x}🙌`)}>
              <Smile />
            </button>
            <span className="sp" />
            <button type="submit" className="btn btn-p sm" data-tooltip="Send comment" data-shortcut="⌘↵" onMouseDown={(e) => e.preventDefault()} disabled={sending || !text.trim()}>
              <SendHorizontal />Send
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
